import React from 'react';

import { Alert, Button, Group, Modal, Stack, Text, ThemeIcon, Title, useMantineTheme } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import useDeleteDemoGym from 'hooks/demo-gym/useDeleteDemoGym';
import { DemoGymList } from 'types/generated';
import notificationHandler from 'utils/notifications';

const DeleteDemoGymModal = ({
  opened,
  demoGym,
  handleClose,
}: {
  opened: boolean;
  handleClose: () => void;
  demoGym: DemoGymList;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { successNotification, errorNotification } = notificationHandler();
  const deleteDemoGymMutation = useDeleteDemoGym();

  const handleDelete = (): void => {
    deleteDemoGymMutation.mutate(demoGym.demoGymId, {
      onSuccess: () => {
        successNotification('Demo gym delete success', `${demoGym.gym.title} successfully deleted.`);
      },
      onError: error => {
        errorNotification('Demo gym delete failed', error.response?.data.message);
      },
      onSettled: () => {
        handleClose();
      },
    });
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleClose}
      title={
        <Group>
          <ThemeIcon variant='light' color='red' size='xl'>
            <IconAlertTriangle style={{ width: '70%', height: '70%' }} />
          </ThemeIcon>
          <Title order={2} fw={600}>
            Delete Demo Account
          </Title>
        </Group>
      }
      size='lg'
      padding='xl'
      radius='lg'
    >
      <Stack spacing='xl'>
        <Text>Are you sure you want to delete the demo account for</Text>
        <Alert color='red' variant='outline' sx={{ background: theme.colors.red[2] }}>
          <Text color={theme.colors.red[9]}>
            This account cannot be undone. All account data will be permanently deleted.
          </Text>
        </Alert>

        <Stack spacing='xs'>
          <Text>
            <Text span fw={500}>
              Owner:
            </Text>{' '}
            {demoGym.gym.owner.fullName}
          </Text>
          <Text>
            <Text span fw={500}>
              Email:
            </Text>{' '}
            {demoGym.gym.owner.email}
          </Text>
          <Text>
            <Text span fw={500}>
              Status:
            </Text>{' '}
            {demoGym.isActive ? 'Active' : 'Deactivated'}
          </Text>
        </Stack>

        <Group spacing='xs' position='right' grow>
          <Button size='md' variant='default' onClick={handleClose}>
            Cancel
          </Button>
          <Button size='md' onClick={handleDelete} color='red' loading={deleteDemoGymMutation.isLoading}>
            Delete Demo Account
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default DeleteDemoGymModal;
